import React from 'react'
import styled, { css } from 'styled-components'
import { Link, useNavigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'

import { Icon, Text, TextButton, Toast, LoadingModal } from 'components'
import { ROUTES, ROUTE_BASE } from '../../constants/constants'

import useModal from 'hooks/use-modal'
import { openFromUrl } from 'utils/file'
import { logout } from 'services/auth'
import { getUserGuide } from 'services/user-guide'
import { FETCHING_STATUS } from 'utils/constants'
import { useAppDispatch } from 'store'
import useLocalStorage from 'hooks/use-local-storage'
import useLanguage from 'queries/use-language'
import { LANGUAGE } from 'utils/constants/locale'

import { profileOptions } from './with-navigation-bar'

type Props = {
  branchName?: string
  onToggleOpenMenu?: () => void
  onChangeBranch: () => void
  onChangeLanguage: () => void
}

const CustomProfileDropdownItems: React.FC<Props> = ({
  branchName,
  onToggleOpenMenu,
  onChangeBranch,
  onChangeLanguage,
}) => {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const dispatch = useAppDispatch()

  const { languageOptions } = useLanguage()

  const [locale] = useLocalStorage('LANGUAGE_SELECTION', LANGUAGE.en)
  const { setModal } = useModal()

  const currentLanguage = languageOptions.find((item) => item.value === locale)

  const handleLogout = async () => {
    setModal(<LoadingModal message="welcome_logging_out" />)
    await dispatch(logout())
    setModal(null)
    navigate(`/${ROUTE_BASE.AUTH}/${ROUTES.LOGIN}`)
  }

  const handleSelected = (value: string) => {
    onToggleOpenMenu?.()

    switch (value) {
      case 'help':
        openUserGuide()
        break
      case 'logout':
        return handleLogout()
      default:
        return null
    }
  }

  const openUserGuide = async () => {
    const response = await getUserGuide()

    if (response.status === FETCHING_STATUS.success && response.data.link) {
      openFromUrl(response.data.link)
      return
    }

    Toast.toastMessage({ message: 'user_guide_error', variant: 'error' })
  }

  return (
    <Container>
      <BranchRow>
        <Text>{branchName}</Text>
        <TextButton onClick={onChangeBranch}>change_lbl</TextButton>
      </BranchRow>
      <BranchRow>
        <Text>{t('language_value', { value: currentLanguage?.label })}</Text>
        <TextButton onClick={onChangeLanguage}>change_lbl</TextButton>
      </BranchRow>
      <NavDropdownItem to={`/${ROUTE_BASE.SETTINGS}`}>
        <Icon icon={['far', 'cog']} />
        <Text>setting_btn</Text>
      </NavDropdownItem>
      {profileOptions.map((item) => (
        <OptionRow key={item.label} onClick={() => handleSelected(item.value)}>
          <Icon icon={item.icon} />
          <Text>{item.label}</Text>
        </OptionRow>
      ))}
    </Container>
  )
}

export default CustomProfileDropdownItems

const Container = styled.div``

const RowCss = css`
  ${(p) => p.theme.font({ size: 14 })}
  display: flex;
  gap: 1rem;
  align-items: center;
  padding: 16px;
  position: relative;
  text-align: left;
  user-select: none;
  color: ${(p) => p.theme.colors.defaultText};
  background: ${(p) => p.theme.colors.white};
  border-top: 1px solid ${(p) => p.theme.colors.gray3};
`

const BranchRow = styled.div`
  ${RowCss}
  justify-content: space-between;
`

const OptionRow = styled.div`
  ${RowCss}

  &:hover {
    background: ${(p) => p.theme.colors.gray1};
    cursor: pointer;
  }
`

const NavDropdownItem = styled(Link)`
  ${RowCss}
  text-decoration: none;

  &:hover {
    background: ${(p) => p.theme.colors.gray1};
    cursor: pointer;
  }
`
